import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/atoms/Accordion'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@frontend/utils/I18n'
import timeFormatter from '@frontend/utils/timeFormatter'
import type { CompletedCycleType } from '@frontend/graphql/queries/callingSession'
import { SplitStatCard, BreakdownLine } from '@components/molecules/StatCard'

const t = scopedTranslation('components.impact_dashboard')

function formatCycleDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  return timeFormatter.fromISO(dateString).dateTime.toFormat('d MMMM')
}

function formatCycleTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  return timeFormatter.fromISO(dateString).toHoursMinutes()
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return ''
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return t('journey.duration_hours', { count: hours })
  return t('journey.duration_minutes', { count: minutes })
}

type CycleAccordionItemProps = {
  cycle: CompletedCycleType
}

export function CycleAccordionItem({ cycle }: CycleAccordionItemProps) {
  const title = t('journey.completed', {
    number: cycle.cycleNumber,
    date: formatCycleDate(cycle.finishedAt),
  })

  const remainingCount = cycle.callsAttemptedCount - cycle.completedSupportersCount

  return (
    <AccordionItem value={cycle.id} className="tw:border tw:border-gray-200 tw:rounded-xl tw:bg-white tw:mb-3">
      <AccordionTrigger className="tw:px-5 tw:py-4 tw:hover:no-underline">
        <div className="tw:flex tw:flex-col tw:gap-1.5 tw:text-left tw:flex-1 tw:min-w-0">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Icon type="refresh-cw-04" className="tw:h-5 tw:w-5 tw:text-purple-600 tw:shrink-0" />
            <span className="tw:text-sm tw:xl:text-base tw:font-semibold tw:text-gray-900">{title}</span>
          </div>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-0.5 tw:text-xs tw:xl:text-sm tw:ml-7">
            <span className="tw:text-purple-600 tw:font-semibold">{t('journey.calls_attempted', { count: cycle.callsAttemptedCount })}</span>
            {cycle.startedAt && cycle.finishedAt && (
              <>
                <span className="tw:text-gray-400">·</span>
                <span className="tw:text-gray-900 tw:font-normal">{formatCycleTime(cycle.startedAt)} – {formatCycleTime(cycle.finishedAt)}</span>
              </>
            )}
            {cycle.durationSeconds && (
              <>
                <span className="tw:text-gray-400">·</span>
                <span className="tw:text-gray-900 tw:font-normal">{formatDuration(cycle.durationSeconds)}</span>
              </>
            )}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="tw:px-5">
        <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3">
          <SplitStatCard value={cycle.completedSupportersCount} label={t('stats.completed')} size="sm">
            <BreakdownLine icon="check-circle" iconColor="tw:text-green-600" label={`${cycle.successfulCount} ${t('stats.successful')}`} size="sm" />
            <BreakdownLine icon="x-circle" iconColor="tw:text-red-600" label={`${cycle.unsuccessfulCount} ${t('stats.unsuccessful')}`} size="sm" />
          </SplitStatCard>

          <SplitStatCard value={remainingCount} label={t('journey.remaining')} size="sm">
            <BreakdownLine icon="phone-call-01" iconColor="tw:text-blue-600" label={`${cycle.callsAttemptedCount} ${t('journey.call_attempts')}`} size="sm" />
            <BreakdownLine icon="phone-off" iconColor="tw:text-gray-500" label={`${remainingCount} ${t('journey.unavailable')}`} size="sm" />
          </SplitStatCard>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}
